import { useEffect, useRef, useState } from "react";
import { X, MapPin } from "lucide-react";
import { createPortal } from "react-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import api from "@/lib/api";

// Webpack can't resolve Leaflet's default marker assets on its own.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const EVENT_LABELS = {
  clock_in: "Clock in",
  clock_out: "Clock out",
  lunch_start: "Lunch start",
  lunch_end: "Lunch end",
  break_start: "Break start",
  break_end: "Break end",
  travel: "Travel",
  travel_end: "Arrived",
};
const EVENT_COLORS = {
  clock_in: "bg-emerald-500",
  clock_out: "bg-red-500",
  lunch_start: "bg-amber-500",
  lunch_end: "bg-sky-500",
  break_start: "bg-amber-500",
  break_end: "bg-sky-500",
  travel: "bg-[#1a3fd1]",
  travel_end: "bg-emerald-500",
};

// Popup HTML is built by hand — escape anything that came from user input.
const esc = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/**
 * Day map for an employee's punches — opened by tapping a punch/geo
 * notification. Drops a pin for every punch that logged GPS on OpenStreetMap
 * (travel punches get a blue dot + their reason in the popup; "Arrived"
 * travel_end pins get a green dot + a PERMANENT on-map label with the
 * job site they ended at) and draws a dashed route line through the day's
 * points in time order, so the office sees the drive between sites, not
 * just isolated pins.
 */
const PunchMapDialog = ({ employeeId, day, title, sender, onClose, testid = "punch-map" }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const mapEl = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data: d } = await api.get(`/notifications/punch-map/${employeeId}/${day}`);
        if (live) setData(d);
      } catch (e) {
        if (live) setError(e?.response?.data?.detail || "Could not load locations");
      }
    })();
    return () => { live = false; };
  }, [employeeId, day]);

  useEffect(() => {
    if (!data || !mapEl.current || mapRef.current) return undefined;
    const pts = (data.punches || []).filter((p) => p.lat != null && p.lng != null);
    const map = L.map(mapEl.current, { attributionControl: true });
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    if (pts.length > 0) {
      const bounds = [];
      const dot = (color) => L.divIcon({
        className: "",
        html: `<div style="width:14px;height:14px;border-radius:9999px;background:${color};border:2.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45)"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      const travelIcon = dot("#1a3fd1");
      const arriveIcon = dot("#10b981");
      pts.forEach((p) => {
        const ll = [p.lat, p.lng];
        bounds.push(ll);
        const isTravel = p.event === "travel";
        const isArrive = p.event === "travel_end";
        const note = isTravel && p.note ? `<br/><i>&ldquo;${esc(p.note)}&rdquo;</i>` : "";
        const marker = L.marker(ll, (isTravel || isArrive) ? { icon: isTravel ? travelIcon : arriveIcon } : {})
          .addTo(map)
          .bindPopup(
            `<b>${esc(EVENT_LABELS[p.event] || p.event)}</b><br/>${esc(p.time || "")}${p.job_name ? ` · ${esc(p.job_name)}` : ""}${note}`,
          );
        // Dispatch needs to see where every drive ENDED without tapping pins:
        // an Arrived pin that carries a job gets a permanent on-map label.
        if (isArrive && p.job_name) {
          marker.bindTooltip(esc(p.job_name), {
            permanent: true, direction: "top", offset: [0, -10], className: "punch-map-tip",
          });
        }
      });
      // Route line through the day's located punches in chronological order
      // (backend sorts by timestamp) — travel pins sit on it so the office
      // sees the drive between sites, not just isolated pins.
      if (pts.length > 1) {
        L.polyline(pts.map((p) => [p.lat, p.lng]), {
          color: "#1a3fd1", weight: 3, opacity: 0.7, dashArray: "6 8", className: "day-route",
        }).addTo(map);
      }
      map.fitBounds(bounds, { padding: [45, 45], maxZoom: 16 });
    } else {
      map.setView([43.6532, -79.3832], 9); // GTA fallback when nothing logged GPS
    }
    return () => { map.remove(); mapRef.current = null; };
  }, [data]);

  const located = (data?.punches || []).filter((p) => p.lat != null && p.lng != null);
  const unlocated = (data?.punches || []).filter((p) => p.lat == null || p.lng == null);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid={`${testid}-modal`}>
      <style>{`.punch-map-tip{background:#0b0b0f;color:#6ee7b7;border:1px solid rgba(16,185,129,.55);border-radius:6px;font:600 10px/1.3 ui-monospace,SFMono-Regular,Menlo,monospace;text-transform:uppercase;letter-spacing:.08em;padding:3px 7px;box-shadow:0 2px 6px rgba(0,0,0,.55);white-space:nowrap}.punch-map-tip:before{display:none}`}</style>
      <div
        className="w-full max-w-lg overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-3.5">
          <img src="/api/branding/icon/elite-logo-dark.png" alt="" className="logo-dark-only h-8 w-auto object-contain" />
          <img src="/api/branding/icon/elite-logo-light.png" alt="" className="logo-light-only h-8 w-auto object-contain" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#8fa4f0]">{sender || "Time clock"}</p>
            <p className="truncate text-sm font-semibold text-white" data-testid={`${testid}-title`}>{title}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid={`${testid}-close`} className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        {error && <p className="px-5 py-6 text-center text-sm text-red-400">{error}</p>}
        {!data && !error && <p className="px-5 py-6 text-center text-sm text-white/40">Loading locations…</p>}

        {data && (
          <>
            <div className="max-h-36 overflow-y-auto border-b border-white/10 px-5 py-3" data-testid={`${testid}-list`}>
              <ul className="space-y-1.5">
                {data.punches.length === 0 && (
                  <li className="text-xs text-white/40">No punches logged this day.</li>
                )}
                {data.punches.map((p, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs" data-testid={`${testid}-pin-${i}`}>
                    <span className={`h-2 w-2 shrink-0 rounded-full ${EVENT_COLORS[p.event] || "bg-white/40"}`} />
                    <span className="font-mono uppercase tracking-widest text-white/70">{EVENT_LABELS[p.event] || p.event}</span>
                    <span className="text-white/50">{p.time}</span>
                    {p.job_name && (
                      <span
                        className={`truncate ${p.event === "travel_end" ? "font-semibold text-emerald-600 dark:text-emerald-300" : "text-white/40"}`}
                        data-testid={p.event === "travel_end" ? `${testid}-arrived-job-${i}` : undefined}
                      >
                        · {p.job_name}
                      </span>
                    )}
                    {p.note && (
                      <span className="truncate italic text-white/50" data-testid={`${testid}-note-${i}`}>&ldquo;{p.note}&rdquo;</span>
                    )}
                    {p.lat != null && p.lng != null ? (
                      <span className="ml-auto shrink-0 font-mono text-[10px] text-white/35">{p.lat.toFixed(4)},{p.lng.toFixed(4)}</span>
                    ) : (
                      <span className="ml-auto shrink-0 font-mono text-[10px] text-amber-400/70">no GPS</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            {located.length > 0 ? (
              <div ref={mapEl} className="h-72 w-full sm:h-80" data-testid={`${testid}-leaflet`} />
            ) : (
              <div className="flex h-40 flex-col items-center justify-center gap-2 text-white/40">
                <MapPin className="h-6 w-6" />
                <p className="text-xs">No GPS locations were logged for these punches.</p>
              </div>
            )}
            {unlocated.length > 0 && located.length > 0 && (
              <p className="px-5 py-2 font-mono text-[10px] text-amber-400/70">
                {unlocated.length} punch{unlocated.length === 1 ? "" : "es"} had no GPS and aren't pinned.
              </p>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default PunchMapDialog;
